import { useMemo } from "react";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import { HgCountry, HgRegion, HgState } from "../../../../types/taxonomy";
import { SAMConfig } from "../../../../types/workspace";
import { FilteredGeographyData, FilterLabel } from "../types";
import { getLabelText, getTotalCounts } from "../utils";

function mapIdsToData<T>(ids: string[], dataMap: Map<string, T>): T[] {
  return ids.map((id) => dataMap.get(id)).filter(Boolean) as T[];
}

export const useGeographyData = (filters: SAMConfig["geography"]) => {
  const { countryDataMap, regionDataMap, stateDataMap, statesLoading } =
    useTaxonomyContext();

  const includedCountries = useMemo<HgCountry[]>(() => {
    return mapIdsToData(filters.countryIds, countryDataMap);
  }, [filters.countryIds, countryDataMap]);

  const excludedCountries = useMemo<HgCountry[]>(() => {
    return mapIdsToData(filters.excludedCountryIds, countryDataMap);
  }, [filters.excludedCountryIds, countryDataMap]);

  const includedRegions = useMemo<HgRegion[]>(() => {
    return mapIdsToData(filters.regionsIds, regionDataMap);
  }, [filters.regionsIds, regionDataMap]);

  const includedStates = useMemo<HgState[]>(() => {
    return mapIdsToData(filters.stateIds, stateDataMap);
  }, [filters.stateIds, stateDataMap]);

  const geographyData = useMemo(
    (): FilteredGeographyData => ({
      countries: {
        included: includedCountries,
        excluded: excludedCountries,
      },
      regions: {
        included: includedRegions,
        excluded: [],
      },
      states: {
        included: includedStates,
        excluded: [],
      },
    }),
    [includedCountries, excludedCountries, includedRegions, includedStates]
  );

  const labels = useMemo((): FilterLabel[] => {
    const { includedCount, excludedCount } = getTotalCounts(geographyData);
    const labelText = getLabelText("Geography", includedCount, excludedCount);
    return labelText ? [{ text: labelText, type: "geography" }] : [];
  }, [geographyData]);

  return {
    geographyData,
    isLoading: statesLoading,
    labels,
  };
};
